/* eslint-disable max-lines */
import { useSelector } from 'react-redux'
import { useEffect, useState } from 'react'
import { useI18n, formatCurrency } from '@popety_io/popety-io-lib'
import { Box, Typography } from '@mui/material'
import Switch from '@mui/material/Switch'
import Tooltip from '@mui/material/Tooltip'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'

import MouseHoverPopover from '../../../components/MouseHoverPopover'
import { estimateLoadingSelector } from '../../Estimate/redux'

const PotetialSolaireSection = ({
  building,
  solarAverage,
  solarAverageColor,
  type,
  toggleDisplay,
  defaultShow = true,
}: {
  building: any
  solarAverage: any
  solarAverageColor: any
  type?: string
  toggleDisplay?: (name: string) => (checked: boolean) => void
  defaultShow?: boolean
}) => {
  const [show, setShow] = useState(defaultShow)
  const { t } = useI18n()

  const isLoading = useSelector(estimateLoadingSelector)

  useEffect(() => {
    setShow(defaultShow)
  }, [defaultShow])

  const onToggle = (checked: boolean) => {
    toggleDisplay?.('potentialSolaire')(checked)
    setShow(checked)
  }

  return (
    <Box paddingY={2} display="flex" flexDirection="column" gap={4}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            marginBottom: '4px',
            fontSize: '1.1rem',
            textTransform: 'uppercase',
          }}
        >
          {t('building.energy.Potential Solaire toiture')}
        </Typography>
        {type === 'estimate' && (
          <Tooltip
            title={
              show ? t('common.disable_button_tooltip') : t('common.activate')
            }
          >
            <Switch
              checked={show}
              onChange={(e) => onToggle(e.target.checked)}
              size="small"
              disabled={isLoading}
            />
          </Tooltip>
        )}
      </Box>
      {show && (
        <>
          <Grid container spacing={{ xs: 1, sm: 3 }} alignItems="flex-start">
            <Grid item container xs={12} sm={6} md={4}>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                sx={{
                  flexGrow: 1,
                }}
              >
                <Typography
                  color={solarAverageColor as string}
                  variant="h5"
                  fontSize="1.846rem"
                  fontWeight="500"
                >
                  {solarAverage || '-'}
                </Typography>
                <Box display="flex" alignItems="center" justifyContent="center">
                  <MouseHoverPopover
                    title={t('common.Aptitude Solaire')}
                    sx={{ display: 'flex', gap: 1 }}
                  >
                    <Paper
                      sx={{
                        padding: 2,
                        width: 'fit-content',
                        maxWidth: '600px',
                        'div > span': {
                          padding: '3px',
                        },
                      }}
                    >
                      <Box
                        gap="2px"
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <Typography component="span" color="text.secondary">
                          {t('land.sunlight.aptitudeSolaire-desc1')}
                        </Typography>
                        <Typography component="span" color="text.secondary">
                          {t('land.sunlight.aptitudeSolaire-desc2')}
                        </Typography>
                        <Typography component="span" color="text.secondary">
                          {t('land.sunlight.aptitudeSolaire-desc-weak')}
                        </Typography>
                        <Typography component="span" color="text.secondary">
                          {t('land.sunlight.aptitudeSolaire-desc-mid')}
                        </Typography>
                        <Typography component="span" color="text.secondary">
                          {t('land.sunlight.aptitudeSolaire-desc-good')}
                        </Typography>
                        <Typography component="span" color="text.secondary">
                          {t('land.sunlight.aptitudeSolaire-desc-very-good')}
                        </Typography>
                        <Typography component="span" color="text.secondary">
                          {t('land.sunlight.aptitudeSolaire-desc-excellent')}
                        </Typography>
                      </Box>
                    </Paper>
                  </MouseHoverPopover>
                </Box>
              </Box>
            </Grid>
            <Grid item container xs={12} sm={6} md={4}>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                sx={{
                  flexGrow: 1,
                }}
              >
                <Typography variant="h5" fontWeight="500" fontSize="1.846rem">
                  {!building?.average_exposure
                    ? '-'
                    : `${formatCurrency(
                        building?.average_exposure || 0,
                        '',
                      )} kWh/m2`}
                </Typography>
                <Box display="flex" alignItems="center" justifyContent="center">
                  <MouseHoverPopover
                    title={t('land.sun.Exposition moyenne')}
                    sx={{ display: 'flex', gap: 1 }}
                  >
                    <Paper
                      sx={{
                        padding: 2,
                        width: 'fit-content',
                        maxWidth: '600px',
                        'div > span': {
                          padding: '3px',
                        },
                      }}
                    >
                      <Box
                        gap="2px"
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <Typography component="span" color="text.secondary">
                          {t('land.sunlight.expositionMoyenne-desc')}
                        </Typography>
                      </Box>
                    </Paper>
                  </MouseHoverPopover>
                </Box>
              </Box>
            </Grid>
            <Grid item container xs={12} sm={6} md={4}>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                sx={{
                  flexGrow: 1,
                }}
              >
                <Typography variant="h5" fontWeight="500" fontSize="1.846rem">
                  {!building?.roof_area
                    ? '-'
                    : `${formatCurrency(building?.roof_area || 0, '')} m2`}
                </Typography>
                <Box display="flex" alignItems="center" justifyContent="center">
                  <MouseHoverPopover
                    title={t('land.sun.Surface toits')}
                    sx={{ display: 'flex', gap: 1 }}
                  >
                    <Paper
                      sx={{
                        padding: 2,
                        width: 'fit-content',
                        maxWidth: '600px',
                        'div > span': {
                          padding: '3px',
                        },
                      }}
                    >
                      <Box
                        gap="2px"
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <Typography component="span" color="text.secondary">
                          {t('land.sunlight.Surface toits-desc')}
                        </Typography>
                      </Box>
                    </Paper>
                  </MouseHoverPopover>
                </Box>
              </Box>
            </Grid>
            <Grid item container xs={12} sm={6} md={4}>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                sx={{
                  flexGrow: 1,
                }}
              >
                <Typography variant="h5" fontWeight="500" fontSize="1.846rem">
                  {!building?.global_exposure
                    ? '-'
                    : `${formatCurrency(
                        building?.global_exposure || 0,
                        '',
                      )} kWh`}
                </Typography>
                <Box display="flex" alignItems="center" justifyContent="center">
                  <MouseHoverPopover
                    title={t('land.sun.Exposition globale')}
                    sx={{ display: 'flex', gap: 1 }}
                  >
                    <Paper
                      sx={{
                        padding: 2,
                        width: 'fit-content',
                        maxWidth: '600px',
                        'div > span': {
                          padding: '3px',
                        },
                      }}
                    >
                      <Box
                        gap="2px"
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <Typography component="span" color="text.secondary">
                          {t('land.sun.Exposition globale-desc')}
                        </Typography>
                      </Box>
                    </Paper>
                  </MouseHoverPopover>
                </Box>
              </Box>
            </Grid>
            <Grid item container xs={12} sm={6} md={4}>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                sx={{
                  flexGrow: 1,
                }}
              >
                <Typography variant="h5" fontWeight="500" fontSize="1.846rem">
                  {!building?.electrical_efficiency
                    ? '-'
                    : `${formatCurrency(
                        building?.electrical_efficiency || 0,
                        '',
                      )} kWh`}
                </Typography>
                <Box display="flex" alignItems="center" justifyContent="center">
                  <MouseHoverPopover
                    title={`${t('land.sun.Rendement électrique')}`}
                    sx={{ display: 'flex', gap: 1, textAlign: 'center' }}
                  >
                    <Paper
                      sx={{
                        padding: 2,
                        width: 'fit-content',
                        maxWidth: '600px',
                        'div > span': {
                          padding: '3px',
                        },
                      }}
                    >
                      <Box
                        gap="2px"
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <Typography component="span" color="text.secondary">
                          {t('land.sun.Rendement électrique-desc').replace(
                            /(;|:)/g,
                            '$1\n',
                          )}
                        </Typography>
                      </Box>
                    </Paper>
                  </MouseHoverPopover>
                </Box>
              </Box>
            </Grid>
            <Grid item container xs={12} sm={6} md={4}>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                sx={{
                  flexGrow: 1,
                }}
              >
                <Typography variant="h5" fontWeight="500" fontSize="1.846rem">
                  {!building?.thermal_efficiency
                    ? '-'
                    : `${formatCurrency(
                        building?.thermal_efficiency || 0,
                        '',
                      )} kWh`}
                </Typography>
                <Box display="flex" alignItems="center" justifyContent="center">
                  <MouseHoverPopover
                    title={t('land.sun.Rendement thermique')}
                    sx={{ display: 'flex', gap: 1 }}
                  >
                    <Paper
                      sx={{
                        padding: 2,
                        width: 'fit-content',
                        maxWidth: '600px',
                        'div > span': {
                          padding: '3px',
                        },
                      }}
                    >
                      <Box
                        gap="2px"
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <Typography component="span" color="text.secondary">
                          {t('land.sun.Rendement thermique-desc').replace(
                            /(;|:)/g,
                            '$1\n',
                          )}
                        </Typography>
                      </Box>
                    </Paper>
                  </MouseHoverPopover>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  )
}

export default PotetialSolaireSection
